import { Button } from '~/components/ui/button';
import { Checkbox, Field, Input, Label, Separator } from '~/components/ui/primitives';
import { usePackages, useProducts } from '~/lib/queries';
import { formatPrice, formatRelativeDay, formatTime } from '~/lib/utils';
import type { BookingDraft } from '../booking-types';
import { StepShell } from '../step-shell';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function SummaryStep({
  draft,
  patch,
  errors,
  acceptTerms,
  setAcceptTerms,
  isAuthenticated,
  userName,
  submitting,
  onBack,
  onSubmit,
}: {
  draft: BookingDraft;
  patch: (values: Partial<BookingDraft>) => void;
  errors: Record<string, string>;
  acceptTerms: boolean;
  setAcceptTerms: (value: boolean) => void;
  isAuthenticated: boolean;
  userName?: string;
  submitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const { data: packages } = usePackages();
  const { data: products } = useProducts();

  const selectedPackage = packages?.find((item) => item.id === draft.packageId);

  const productLines = draft.products
    .map((line) => {
      const product = products?.find((item) => item.id === line.productId);
      return product ? { ...line, name: product.name, price: product.price } : null;
    })
    .filter((line): line is NonNullable<typeof line> => line !== null);

  const total =
    (selectedPackage?.price ?? 0) +
    productLines.reduce((sum, line) => sum + line.price * line.quantity, 0);

  return (
    <StepShell
      title="Recapitulatif"
      description="Verifiez avant de confirmer. Vous pourrez annuler sans frais jusqu'a 24 h avant."
      onBack={onBack}
    >
      <dl className="flex flex-col gap-2 text-sm">
        <div className="app-summary-row">
          <dt className="text-muted-foreground">Adresse</dt>
          <dd className="text-right font-medium">{draft.addressLabel}</dd>
        </div>
        <div className="app-summary-row">
          <dt className="text-muted-foreground">Cycle</dt>
          <dd className="text-right font-medium">{draft.bicycleLabel}</dd>
        </div>
        <div className="app-summary-row">
          <dt className="text-muted-foreground">Forfait</dt>
          <dd className="text-right font-medium">{selectedPackage?.name}</dd>
        </div>
        <div className="app-summary-row">
          <dt className="text-muted-foreground">Rendez-vous</dt>
          <dd className="text-right font-medium">
            {draft.slotStartsAt ? (
              <>
                <span className="capitalize">{formatRelativeDay(draft.slotStartsAt)}</span>
                {' a '}
                {formatTime(draft.slotStartsAt)}
              </>
            ) : null}
          </dd>
        </div>
        <div className="app-summary-row">
          <dt className="text-muted-foreground">Technicien</dt>
          <dd className="text-right font-medium">{draft.technicianName}</dd>
        </div>
      </dl>

      <Separator />

      <div className="space-y-1.5 text-sm">
        <div className="app-summary-row">
          <span>{selectedPackage?.name}</span>
          <span className="tabular-nums">{formatPrice(selectedPackage?.price ?? 0)}</span>
        </div>
        {productLines.map((line) => (
          <div key={line.productId} className="app-summary-row text-muted-foreground">
            <span>
              {line.name} × {line.quantity}
            </span>
            <span className="tabular-nums">{formatPrice(line.price * line.quantity)}</span>
          </div>
        ))}
        <Separator className="my-1" />
        <div className="app-summary-row text-base font-semibold">
          <span>Total</span>
          <span className="tabular-nums">{formatPrice(total)}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Reglement sur place a l'issue de l'intervention.
        </p>
      </div>

      <Separator />

      {/* RG-04 : sans compte, seules les coordonnees de contact sont demandees.
          Le mot de passe viendra apres la confirmation. */}
      {isAuthenticated ? (
        <p className="text-sm text-muted-foreground">
          Rendez-vous au nom de <span className="font-medium text-foreground">{userName}</span>.
        </p>
      ) : (
        <div className="space-y-3">
          <p className="app-section-title">Vos coordonnees</p>
          <p className="text-sm text-muted-foreground">
            Pas besoin de creer un compte maintenant : nous vous le proposerons juste apres.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Prenom"
              htmlFor="guest-firstName"
              error={errors['guest.firstName']}
              required
            >
              <Input
                value={draft.guest.firstName}
                onChange={(event) =>
                  patch({ guest: { ...draft.guest, firstName: event.target.value } })
                }
                autoComplete="given-name"
              />
            </Field>
            <Field label="Nom" htmlFor="guest-lastName" error={errors['guest.lastName']} required>
              <Input
                value={draft.guest.lastName}
                onChange={(event) =>
                  patch({ guest: { ...draft.guest, lastName: event.target.value } })
                }
                autoComplete="family-name"
              />
            </Field>
            <Field
              label="Adresse e-mail"
              htmlFor="guest-email"
              error={errors['guest.email']}
              required
            >
              <Input
                type="email"
                value={draft.guest.email}
                onChange={(event) =>
                  patch({ guest: { ...draft.guest, email: event.target.value } })
                }
                autoComplete="email"
              />
            </Field>
            <Field
              label="Telephone"
              htmlFor="guest-phone"
              error={errors['guest.phone']}
              hint="Pour vous prevenir en cas d'imprevu."
              required
            >
              <Input
                type="tel"
                value={draft.guest.phone}
                onChange={(event) =>
                  patch({ guest: { ...draft.guest, phone: event.target.value } })
                }
                autoComplete="tel"
                placeholder="06 12 34 56 78"
              />
            </Field>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2">
        <Checkbox
          id="cgv"
          checked={acceptTerms}
          onCheckedChange={(checked) => setAcceptTerms(checked === true)}
          aria-invalid={errors.acceptTerms ? true : undefined}
        />
        <Label htmlFor="cgv" className="text-sm font-normal leading-snug">
          J'accepte les conditions generales de vente.
        </Label>
      </div>
      {errors.acceptTerms ? (
        <p role="alert" className="-mt-2 text-xs font-medium text-destructive">
          {errors.acceptTerms}
        </p>
      ) : null}

      <Button size="lg" className="w-full" loading={submitting} onClick={onSubmit}>
        Confirmer le rendez-vous
      </Button>
    </StepShell>
  );
}
